import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Modal from "components/Modal";
import Button from "Shared/Button";
import ItemImage from "Shared/ItemImage";
import styles from "./CartEditModal.module.css";
import { getSizeAbbreviation, getSizeOrder } from "utils/sizeOrder";

import cartIcon from "assets/CartHeader.svg";

const DEFAULT_SIZE_OPTIONS = [];

const DEFAULT_COLOR_OPTIONS = [];

export function CartEditModal({
  isOpen,
  onClose,
  onSave,
  onCancel,
  item,
  sizeOptions,
  colorOptions,
}) {
  const resolvedSizes = sizeOptions ?? DEFAULT_SIZE_OPTIONS;
  const resolvedColors = colorOptions ?? DEFAULT_COLOR_OPTIONS;
  const { t } = useTranslation();

  const [quantity, setQuantity] = useState(1);
  const [size, setSize] = useState(item?.size ?? null);
  const [colorIdx, setColorIdx] = useState(0);
  const [showMaxMessage, setShowMaxMessage] = useState(false);

  // null quantityAvailable means unlimited stock; a number caps how high the
  // stepper can go so a user can't add more than what's actually in stock.
  const maxQuantity =
    typeof item?.quantityAvailable === "number"
      ? Math.max(item.quantityAvailable, 0)
      : null;
  const isAtMaxQuantity = maxQuantity !== null && quantity >= maxQuantity;

  useEffect(() => {
    if (!showMaxMessage) return undefined;
    const timer = setTimeout(() => setShowMaxMessage(false), 3000);
    return () => clearTimeout(timer);
  }, [showMaxMessage]);

  useEffect(() => {
    if (item) {
      const initialQuantity = item.quantity ?? 1;
      const cappedQuantity =
        typeof item.quantityAvailable === "number"
          ? Math.min(initialQuantity, Math.max(item.quantityAvailable, 0))
          : initialQuantity;
      setQuantity(cappedQuantity);
      setShowMaxMessage(false);

      // Default to an in-stock color+size combo rather than always landing
      // on the item's existing color/size (which may be sold out) - matches
      // StockItemDetailsPage's default-selection behavior. Out-of-stock
      // options remain selectable afterward via isSelectionSoldOut.
      const matchedColorIdx = resolvedColors.findIndex(
        (colorOption) =>
          String(colorOption?.label || "").toLowerCase() ===
          String(item?.color || "").toLowerCase()
      );
      const defaultColorIdx =
        matchedColorIdx >= 0 && !resolvedColors[matchedColorIdx]?.isSoldOut
          ? matchedColorIdx
          : (() => {
              const inStockIdx = resolvedColors.findIndex(
                (colorOption) => !colorOption.isSoldOut
              );
              if (inStockIdx >= 0) return inStockIdx;
              return matchedColorIdx >= 0 ? matchedColorIdx : 0;
            })();
      setColorIdx(defaultColorIdx);

      if (!resolvedSizes.length) {
        setSize(item.size ?? null);
        return;
      }

      const defaultColorLabel = resolvedColors[defaultColorIdx]?.label;
      const sizesForDefaultColor =
        defaultColorLabel && item.variantMatrix
          ? resolvedSizes.map((sizeOption) => {
              const key = `${defaultColorLabel}::${sizeOption.label}`;
              const variantEntry = item.variantMatrix[key];
              if (typeof variantEntry?.isAvailable !== "boolean") {
                return sizeOption;
              }
              return { ...sizeOption, isSoldOut: !variantEntry.isAvailable };
            })
          : resolvedSizes;

      const matchedSizeIdx = sizesForDefaultColor.findIndex(
        (sizeOption) => sizeOption.label === item?.size
      );
      const defaultSizeIdx =
        matchedSizeIdx >= 0 && !sizesForDefaultColor[matchedSizeIdx]?.isSoldOut
          ? matchedSizeIdx
          : (() => {
              const inStockIdx = sizesForDefaultColor.findIndex(
                (sizeOption) => !sizeOption.isSoldOut
              );
              if (inStockIdx >= 0) return inStockIdx;
              return matchedSizeIdx >= 0 ? matchedSizeIdx : 0;
            })();
      setSize(sizesForDefaultColor[defaultSizeIdx]?.label ?? item.size ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // Build color→sizes and size→colors maps from variantMatrix for filtering
  const variantFilterMaps = useMemo(() => {
    // For filtering logic, we need to know which colors exist for each size and vice versa
    // The variantMatrix only has variantId/variationId, so we need to extract this info differently
    // Actually, we can derive it from colorOptions and sizeOptions that were passed in

    // Build maps by checking which size options are available for each color
    // and which color options are available for each size
    if (!item?.variantMatrix || Object.keys(item.variantMatrix).length === 0) {
      return { colorToSizes: new Map(), sizeToColors: new Map() };
    }

    const colorToSizes = new Map();
    const sizeToColors = new Map();

    // Parse the variantMatrix keys which are in format "color::size"
    Object.keys(item.variantMatrix).forEach((key) => {
      const [color, size] = key.split("::");

      if (color && size) {
        // Map color → set of sizes
        if (!colorToSizes.has(color)) {
          colorToSizes.set(color, new Set());
        }
        colorToSizes.get(color).add(size);

        // Map size → set of colors
        if (!sizeToColors.has(size)) {
          sizeToColors.set(size, new Set());
        }
        sizeToColors.get(size).add(color);
      }
    });

    return { colorToSizes, sizeToColors };
  }, [item?.variantMatrix]);

  // Filter available colors based on currently selected size
  const availableColors = useMemo(() => {
    if (!size || variantFilterMaps.sizeToColors.size === 0) {
      return resolvedColors;
    }

    const colorsForSize = variantFilterMaps.sizeToColors.get(size);
    if (!colorsForSize) {
      return resolvedColors;
    }

    return resolvedColors.filter((colorOption) =>
      colorsForSize.has(colorOption.label)
    );
  }, [size, resolvedColors, variantFilterMaps]);

  // Filter available sizes based on currently selected color, and refine
  // isSoldOut to the specific color+size combo via variantMatrix (the
  // resolvedSizes flag is a coarse aggregate across all colors).
  const availableSizes = useMemo(() => {
    const currentColor = resolvedColors[colorIdx]?.label;
    const sizesForColor = currentColor
      ? variantFilterMaps.colorToSizes.get(currentColor)
      : null;

    const base =
      currentColor && sizesForColor
        ? resolvedSizes.filter((sizeOption) =>
            sizesForColor.has(sizeOption.label)
          )
        : resolvedSizes;

    if (!currentColor || !item?.variantMatrix) {
      return base;
    }

    return base.map((sizeOption) => {
      const key = `${currentColor}::${sizeOption.label}`;
      const variantEntry = item.variantMatrix[key];
      if (typeof variantEntry?.isAvailable !== "boolean") {
        return sizeOption;
      }
      return { ...sizeOption, isSoldOut: !variantEntry.isAvailable };
    });
  }, [colorIdx, resolvedColors, resolvedSizes, variantFilterMaps, item]);

  const handleSave = () => {
    const selectedColor = resolvedColors[colorIdx]?.label;

    // Look up the variantId from variantMatrix based on selected color and size
    let variantId = item?.variantId ?? null;
    let variationId = item?.variationId ?? null;

    // Try to look up in variantMatrix first (most accurate source)
    if (selectedColor && size && item?.variantMatrix) {
      const key = `${selectedColor}::${size}`;
      const variantData = item.variantMatrix[key];
      if (variantData) {
        // Found exact match in variantMatrix - use this
        variantId = variantData.variantId ?? null;
        variationId = variantData.variationId ?? null;
      }
    }

    onSave?.({
      ...item,
      quantity,
      color: selectedColor,
      size,
      variantId,
      variationId,
    });
    onClose?.();
  };

  // Block saving when the currently selected color/size resolves to an
  // out-of-stock variant (checked via variantMatrix first, falling back to
  // the selected color/size option's own isSoldOut flag).
  const isSelectionSoldOut = useMemo(() => {
    const selectedColor = resolvedColors[colorIdx]?.label;
    if (selectedColor && size && item?.variantMatrix) {
      const variantEntry = item.variantMatrix[`${selectedColor}::${size}`];
      if (typeof variantEntry?.isAvailable === "boolean") {
        return !variantEntry.isAvailable;
      }
    }
    const selectedColorOption = resolvedColors[colorIdx];
    const selectedSizeOption = availableSizes.find(
      (option) => option.label === size
    );
    return Boolean(
      selectedColorOption?.isSoldOut || selectedSizeOption?.isSoldOut
    );
  }, [colorIdx, resolvedColors, size, item, availableSizes]);

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  return (
    <Modal isOpen={isOpen} className={styles.modal}>
      {/* ── Floating cart icon above banner ── */}
      <div className={styles.iconCircleWrap}>
        <div className={styles.iconCircle}>
          {cartIcon ? (
            <img src={cartIcon} alt="cart" className={styles.cartIcon} />
          ) : (
            <span className={styles.cartEmoji}>🛒</span>
          )}
        </div>
      </div>

      {/* ── Dark header banner ── */}
      <div className={styles.header}>
        <span className={styles.headerTitle}>
          {t("cartEditModal.addToCart", "Add To Cart")}
        </span>
      </div>

      {/* ── Subheading / Pre-order banner ── */}
      {item?.isPreOrder ? (
        <div className={styles.preOrderBanner}>
          {t("cartEditModal.preOrder", "THIS ITEM IS AVAILABLE TO PRE-ORDER")}
        </div>
      ) : (
        <div className={styles.subheading}>
          {t("cartEditModal.pleaseSelect", "PLEASE SELECT THE FOLLOWING")}
        </div>
      )}

      <div className={styles.body}>
        {/* ── Color ── */}
        {(item?.isConfigurable || item?.color !== undefined) &&
          resolvedColors.length > 0 && (
            <div className={styles.field}>
              <div className={styles.fieldLabel}>
                {t("cartEditModal.color", "Color")}
              </div>
              <div className={styles.thumbGrid}>
                {availableColors.map((c, i) => {
                  // Find actual index in resolvedColors for selection state
                  const actualIdx = resolvedColors.findIndex(
                    (col) => col.label === c.label
                  );
                  const selected = actualIdx === colorIdx;
                  const isSoldOut = Boolean(c.isSoldOut);
                  // Only use the specific color variant image, don't fall back to main image
                  const imgSrc = c.imageUrl ?? null;
                  return (
                    <button
                      key={`${c.label}-${i}`}
                      type="button"
                      className={`${styles.thumb} ${
                        selected ? styles.thumbSelected : ""
                      } ${isSoldOut ? styles.thumbSoldOut : ""}`}
                      onClick={() => setColorIdx(actualIdx)}
                      aria-label={
                        isSoldOut ? `${c.label} (Out of stock)` : c.label
                      }
                    >
                      <div className={styles.thumbImg}>
                        <ItemImage imageSrc={imgSrc} alt={c.label} isThumb />
                      </div>
                      <div className={styles.thumbLabel}>{c.label}</div>
                      {isSoldOut && (
                        <span
                          className={styles.thumbSoldOutMark}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        {/* ── Book image (no color options) ── */}
        {item?.color === undefined && item?.image && (
          <div className={styles.field}>
            <div className={styles.bookImageWrap}>
              <div className={styles.bookImage}>
                <ItemImage imageSrc={item.image} alt={item.title} isThumb />
              </div>
            </div>
          </div>
        )}

        {/* ── Size ── */}
        {(item?.isConfigurable || item?.size !== undefined) &&
          resolvedSizes.length > 0 && (
            <div className={styles.field}>
              <div className={styles.fieldLabel}>
                {t("cartEditModal.size", "Size")}
              </div>
              <div className={styles.sizeRow}>
                {availableSizes
                  .sort((a, b) => getSizeOrder(a.label) - getSizeOrder(b.label))
                  .map((sizeOption) => {
                    const isSelected = size === sizeOption.label;
                    const isSoldOut = sizeOption.isSoldOut;

                    return (
                      <button
                        key={sizeOption.label}
                        type="button"
                        className={`${styles.sizePill} ${
                          isSelected ? styles.sizeSelected : ""
                        } ${isSoldOut ? styles.sizeSoldOut : ""}`}
                        onClick={() => setSize(sizeOption.label)}
                      >
                        <span className={styles.sizePillText}>
                          {getSizeAbbreviation(sizeOption.label)}
                        </span>
                        {isSoldOut && (
                          <span
                            className={styles.sizeSoldOutMark}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        {/* ── Quantity ── */}
        <div className={styles.field}>
          <div className={styles.fieldLabel}>
            {t("cartEditModal.quantity", "Quantity")}
          </div>
          <div className={styles.stepperWrap}>
            <div className={styles.stepper} aria-label="Quantity selector">
              <div
                className={styles.stepperValue}
                role="status"
                aria-live="polite"
              >
                {quantity}
              </div>
              <div className={styles.stepperChevrons}>
                <button
                  type="button"
                  className={`${styles.stepperChev} ${
                    isAtMaxQuantity ? styles.stepperChevDisabled : ""
                  }`}
                  aria-label="Increase quantity"
                  disabled={isAtMaxQuantity}
                  onClick={() => {
                    const next =
                      maxQuantity !== null
                        ? Math.min(maxQuantity, quantity + 1)
                        : quantity + 1;
                    setQuantity(next);
                    setShowMaxMessage(
                      maxQuantity !== null && next >= maxQuantity
                    );
                  }}
                >
                  <svg viewBox="0 0 24 24" className={styles.stepperChevSvg}>
                    <path
                      d="M7 14l5-5 5 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className={styles.stepperChev}
                  aria-label="Decrease quantity"
                  onClick={() => {
                    setQuantity((q) => Math.max(1, q - 1));
                    setShowMaxMessage(false);
                  }}
                >
                  <svg viewBox="0 0 24 24" className={styles.stepperChevSvg}>
                    <path
                      d="M7 10l5 5 5-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>
            </div>
            {showMaxMessage && isAtMaxQuantity && (
              <div className={styles.maxQuantityAnchor}>
                <span className={styles.maxQuantityBubble} role="alert">
                  {t("cartEditModal.maxQuantity", "Maximum quantity reached.")}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      {isSelectionSoldOut && (
        <div className={styles.outOfStockNotice}>
          {t("cartEditModal.outOfStock", "Out of Stock")}
        </div>
      )}
      <div className={styles.footer}>
        <Button
          text={t("cartEditModal.cancel", "Cancel")}
          onClick={handleCancel}
          className="deleteBtn"
        />
        <Button
          text={t("cartEditModal.addToCart", "Add To Cart")}
          onClick={handleSave}
          className="confirmBtn"
          disabled={isSelectionSoldOut}
        />
      </div>
    </Modal>
  );
}

CartEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  sizeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      isSoldOut: PropTypes.bool,
    })
  ),
  colorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
    })
  ),
  item: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    quantity: PropTypes.number,
    quantityAvailable: PropTypes.number,
    color: PropTypes.string,
    size: PropTypes.string,
    image: PropTypes.any,
  }),
};

export default CartEditModal;
